<div class="flex flex-col gap-4 p-4">
  <div class="flex items-center justify-between">
    <div>
      <h1 class="text-xl font-semibold text-gray-900">API Call Logs</h1>
      <p class="text-sm text-gray-500">Newest first. Hidden maintenance view for venue-quote logging.</p>
    </div>
  </div>

  <div class="flex flex-wrap items-end gap-3">
    <div class="flex flex-col gap-1 min-w-[240px] w-[240px]">
      <label class="text-sm font-semibold text-gray-700">Search</label>
      <input type="text" [formControl]="searchControl" placeholder="path, error, UA..." class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900/50" />
    </div>
    <div class="flex flex-col gap-1 w-40">
      <label class="text-sm font-semibold text-gray-700">Status</label>
      <app-select
        [control]="statusControl"
        [choices]="statusChoices"
        [placeholder]="'Any status'"
        [allowWrap]="false"
        [height]="'10'"
        [multiple]="true"
        [showSelectedTags]="false"
      ></app-select>
    </div>
    <div class="flex flex-col gap-1 w-40">
      <label class="text-sm font-semibold text-gray-700">Method</label>
      <app-select
        [control]="methodControl"
        [choices]="methodChoices"
        [placeholder]="'Any method'"
        [allowWrap]="false"
        [height]="'10'"
        [multiple]="true"
        [showSelectedTags]="false"
      ></app-select>
    </div>
    <div class="flex flex-col gap-1 w-48">
      <label class="text-sm font-semibold text-gray-700">Route</label>
      <app-select
        [control]="routeControl"
        [choices]="routeChoices"
        [placeholder]="'Any route'"
        [allowWrap]="false"
        [height]="'10'"
        [multiple]="true"
        [showSelectedTags]="false"
      ></app-select>
    </div>
    <div class="flex items-center gap-2">
      <button class="px-3 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-800 shadow-sm hover:bg-gray-50" type="button" (click)="clearFilters()">Clear</button>
      <button class="px-3 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-800 shadow-sm hover:bg-gray-50 disabled:opacity-60" type="button" (click)="refresh()" [disabled]="loading">
        {{ loading ? 'Loading…' : 'Refresh' }}
      </button>
    </div>
  </div>

  <div class="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
    <app-data-table
      [dataPage]="dataPage"
      [columns]="columns"
      [hover]="true"
      [loading]="loading"
      [paginate]="true"
      (route)="handleRoute($event)"
      [noDataPlaceholder]="true"
      [rowRouterLinkTemplate]="undefined"
      [rowActions]="[]"
      (rowClick)="selectLog($event)"
      [rowClickable]="true"
      [selectedRowId]="selectedLog?.id"
    />

    @if (selectedLog) {
      <div class="border border-gray-200 rounded-lg p-4 bg-white shadow-sm flex flex-col gap-4">
        <div class="flex items-center justify-between gap-3">
          <h2 class="text-lg font-semibold text-gray-900">Details</h2>
          <div class="flex items-center gap-2">
            @if (copyNotice) {
              <span class="text-xs font-semibold text-green-700 bg-green-100 px-2 py-1 rounded-md">Copied</span>
            }
            <button class="px-3 py-2 text-sm font-medium rounded-md bg-gray-900 text-white disabled:opacity-60 hover:bg-gray-800 cursor-pointer" type="button" (click)="copyDetails()">
              Copy
            </button>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-3 text-sm">
          <div><span class="text-gray-500 block">When</span><span class="font-semibold text-gray-900">{{ formatTimestampShort(selectedLog.created_at) }}</span></div>
          <div><span class="text-gray-500 block">Client IP</span><span class="font-mono text-sm text-gray-900">{{ selectedLog.ip || '—' }}</span></div>
          <div><span class="text-gray-500 block">Route</span><span class="font-semibold text-gray-900">{{ selectedLog.route_name || '—' }}</span></div>
          <div><span class="text-gray-500 block">Status</span><span class="font-semibold text-gray-900">{{ selectedLog.status_code || '—' }}</span></div>
          <div><span class="text-gray-500 block">Path</span><span class="font-mono text-sm text-gray-900">{{ selectedLog.method }} {{ selectedLog.path }}</span></div>
          <div><span class="text-gray-500 block">User Agent</span><span class="font-semibold text-gray-900">{{ selectedLog.user_agent || '—' }}</span></div>
          <div><span class="text-gray-500 block">Venue</span><span class="font-semibold text-gray-900">{{ selectedLog.venue_id || '—' }}</span></div>
          <div><span class="text-gray-500 block">Venue Quote</span><span class="font-semibold text-gray-900">{{ selectedLog.venue_quote_id || '—' }}</span></div>
          <div><span class="text-gray-500 block">Error</span><span class="font-semibold text-gray-900">{{ selectedLog.error_code || selectedLog.error_class || '—' }}</span></div>
          <div><span class="text-gray-500 block">Duration</span><span class="font-semibold text-gray-900">{{ selectedLog.metrics?.duration_ms ? (selectedLog.metrics?.duration_ms | number:'1.0-2') + ' ms' : '—' }}</span></div>
          <div><span class="text-gray-500 block">Request size</span><span class="font-semibold text-gray-900">{{ selectedLog.metrics?.request_size_bytes || '—' }} bytes</span></div>
          <div><span class="text-gray-500 block">Response size</span><span class="font-semibold text-gray-900">{{ selectedLog.metrics?.response_size_bytes || '—' }} bytes</span></div>
        </div>

        <div class="border-t border-gray-200 pt-3">
          <div class="flex flex-wrap gap-2">
            @for (tab of tabs; track tab.id) {
              <button
                type="button"
                class="px-3 py-1.5 text-xs font-semibold rounded-md border cursor-pointer transition"
                [ngClass]="activeTab === tab.id ? 'bg-gray-900 text-white border-gray-900' : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200'"
                (click)="selectTab(tab.id)"
              >
                {{ tab.label }}
              </button>
            }
          </div>
          <div class="mt-3">
            @if (tabPayload) {
              <pre class="bg-slate-900 text-slate-100 rounded-md p-4 text-xs overflow-auto max-h-[520px]">{{ tabPayload | json }}</pre>
            } @else {
              <div class="text-sm text-gray-500">No data.</div>
            }
          </div>
        </div>
      </div>
    }
  </div>
</div>
